// frontend/src/theme/ThemeContext.js
// Light/dark theme state. The actual colors live in index.css as CSS variables
// scoped to <html data-theme="...">. This just toggles that attribute and
// persists the choice. Initial value is applied pre-paint by an inline script
// in public/index.html, so there is no flash of the wrong theme on load.
import React, { createContext, useContext, useState, useEffect } from 'react';

const STORAGE_KEY = 'weaver-theme';
const ThemeContext = createContext(null);

function getInitialTheme() {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'dark';
  } catch {
    return 'dark';
  }
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Keep the browser chrome (mobile status bar) in sync with the surface.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'light' ? '#ffffff' : '#070e1a');
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore persistence failures (e.g. private mode) */
    }
  }, [theme]);

  const toggleTheme = () => setTheme(t => (t === 'light' ? 'dark' : 'light'));

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
